import React, { useEffect, useState } from "react";
import { get_user_list } from "../apis/GetUsers";
import { add_user } from "../apis/AddUser";
import { modify_user } from "../apis/ModifyUser";
import { delete_user } from "../apis/DeleteUser";
import Cookies from "js-cookie";
import { setAPIAuthorizationToken } from "../apis/API";
import "../css/user.scss";
import Loading from "../pages/Loading";
import ConfirmToast from "../pages/Toast";
import NotificationToast from "../Component/Toast";
import AddUserModal from "./AddUserModal";
import { get_user_by_id } from "../apis/GetUserById";
import PaginationFooter from "./PaginationFooter";
import { useTranslation } from "../contexts/LocaleContext";

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [goToValue, setGoToValue] = useState("1");
  const [totalItems, setTotalItems] = useState(0);
  const [toastInfo, setToastInfo] = useState({ message: "", type: "" });

  const fetchUsers = async (
    page = 1,
    perPage = pageSize,
    name = "",
    suppressFetching = false
  ) => {
    try {
      if (!suppressFetching) setIsFetching(true);
      const res = await get_user_list(page, perPage, name, "");
      // Normalize response shapes:
      let items = [];
      let total = 0;
      if (res && res.data) {
        items = res.data.data;
        total = res.data.total || items.length;
      }
      setUsers(items);
      setTotalItems(Number(total) || items.length);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError(err);
    } finally {
      if (!suppressFetching) setIsFetching(false);
    }
  };

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
    fetchUsers(1, pageSize, "");
  }, []);

  // (formatDate removed — role is displayed instead of creation date)

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedUsers = users; // server returns current page

  const goToPage = async (page) => {
    const p = Math.max(1, Math.min(totalPages, page));
    setCurrentPage(p);
    setGoToValue(String(p));
    await fetchUsers(p, pageSize, searchName);
  };

  const onPageSizeChange = async (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setGoToValue("1");
    await fetchUsers(1, size, searchName);
  };

  const onGoToValueChange = (v) => setGoToValue(v);
  const onGoClick = () => {
    const p = Number(goToValue);
    if (!isNaN(p)) {
      goToPage(p);
    }
  };
  // Add-user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const openAddModal = () => {
    setShowAddModal(true);
  };

  const openEditModal = async (user) => {
    const id = user?.id ?? user?._id ?? user?.userId ?? user;
    if (!id) {
      setEditingUser(user || null);
      setShowAddModal(true);
      return;
    }
    try {
      setIsFetching(true);
      const res = await get_user_by_id(id);
      const data = res && res.data ? res.data : null;
      const item = data && data.data ? data.data : data;
      setEditingUser(item || null);
      setShowAddModal(true);
    } catch (err) {
      console.error("Failed to fetch user by id:", err);
      setToastInfo({ message: "Failed to load user details", type: "error" });
    } finally {
      setIsFetching(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setEditingUser(null);
  };

  //显示alert
  const showToast = (message, type) => {
    setToastInfo({ message, type });
  };

  return (
    <div className="users-page ">
      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />
      <div className="users-controls-card">
        <div className="controls-left">
          <div className="search-input">
            <input
              placeholder={t ? t("users.search_placeholder") : "Search here"}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  setGoToValue("1");
                  fetchUsers(1, pageSize, searchName);
                }
              }}
            />
            <button
              type="button"
              className="search-icon"
              aria-label="Search"
              onClick={() => {
                setCurrentPage(1);
                setGoToValue("1");
                fetchUsers(1, pageSize, searchName);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="m16.875 15.455 4 4.005a.5.5 0 0 1-.02.691l-.7.7a.5.5 0 0 1-.71 0l-4-4.004a2.6 2.6 0 0 1-.3-.36l-.75-1.001a7 7 0 0 1-4.37 1.531A7.011 7.011 0 0 1 6.973 3.69a6.995 6.995 0 0 1 8.518 1.971 7.015 7.015 0 0 1-.046 8.753l1 .691q.234.151.43.35M5.025 10.01a5.003 5.003 0 0 0 5 5.006 5 5 0 0 0 3.535-1.466 5 5 0 0 0 1.465-3.54 5.003 5.003 0 0 0-5-5.006c-2.761 0-5 2.241-5 5.006"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="controls-right">
          <button className="btn-add" onClick={openAddModal}>
            {t ? t("users.add") : "Add user"}
          </button>
        </div>
      </div>

      <AddUserModal
        isOpen={showAddModal}
        onClose={closeAddModal}
        initialData={editingUser || undefined}
        onSubmit={async (form) => {
          try {
            setIsSaving(true);
            if (editingUser) {
              // modify existing user; form.id comes from AddUserModal
              await modify_user(
                form.id,
                form.userName,
                form.passWord,
                form.NickName
              );
            } else {
              // create new user
              await add_user(form.userName, form.passWord, form.NickName);
            }
            await fetchUsers(currentPage, pageSize, searchName, true);
            closeAddModal();
          } catch (err) {
            console.error("Failed to save user:", err);
            alert("Failed to save user. Please try again.");
          } finally {
            setIsSaving(false);
          }
        }}
        isLoading={isSaving}
      />
      <ConfirmToast
        show={!!confirmDeleteUser}
        message={
          confirmDeleteUser
            ? t
              ? t("confirm.delete_user.description").replace(
                  "{name}",
                  confirmDeleteUser.name
                )
              : `Delete ${confirmDeleteUser.name}?`
            : ""
        }
        onClose={() => setConfirmDeleteUser(null)}
        confirmProps={{
          title: t ? t("confirm.delete_user.title") : `Delete User`,
          description: confirmDeleteUser
            ? t
              ? t("confirm.delete_user.description").replace(
                  "{name}",
                  confirmDeleteUser.name
                )
              : `Are you sure you want to delete the user "${confirmDeleteUser.name}"?`
            : "",
          confirmLabel: t ? t("confirm.delete") : "Delete",
          cancelLabel: t ? t("confirm.cancel") : "Cancel",
          onCancel: () => setConfirmDeleteUser(null),
          onConfirm: async () => {
            try {
              setIsSaving(true);
              if (confirmDeleteUser) {
                await delete_user(confirmDeleteUser.id);
                showToast(
                  t
                    ? t("notification.user_deleted")
                    : "User deleted successfully!",
                  "success"
                );
                // refresh without showing full-page fetching overlay
                await fetchUsers(currentPage, pageSize, searchName, true);
              }
            } catch (err) {
              console.error("Failed to delete user:", err);
              // fallback small alert if toast not appropriate
              alert(
                t
                  ? t("notification.failed_delete_user")
                  : "Failed to delete user. Please try again."
              );
            } finally {
              setIsSaving(false);
              setConfirmDeleteUser(null);
            }
          },
        }}
      />

      <div className="users-table-card">
        <div className="table-scroll">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t ? t("users.table.account") : "Account"}</th>
                <th>{t ? t("users.table.password") : "Password"}</th>
                <th>{t ? t("users.table.nickname") : "NickName"}</th>
                <th>{t ? t("users.table.role") : "Role"}</th>
                <th>{t ? t("users.table.operation") : "Operation"}</th>
              </tr>
            </thead>
            <tbody>
              {isFetching ? (
                <Loading />
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="5">{t ? t("users.no_items") : "No contacts"}</td>
                </tr>
              ) : (
                paginatedUsers.map((user, idx) => {
                  const globalIndex = (currentPage - 1) * pageSize + idx;
                  return (
                    <tr key={user.userName || globalIndex}>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-value">
                            <div className="user-cell">
                              <div
                                className="user-avatar"
                                aria-hidden="true"
                                title={user.userName}
                              >
                                {user.userName
                                  ? String(user.userName)
                                      .charAt(0)
                                      .toUpperCase()
                                  : "U"}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <span className="user-name">
                                  {user.userName}
                                </span>
                                <div className="user-meta">
                                  <span className="user-id">
                                    {`#${
                                      user.id ||
                                      user._id ||
                                      user.userId ||
                                      globalIndex
                                    }`}
                                  </span>
                                  <span
                                    className={`user-role-badge ${
                                      user.userType === 2
                                        ? "role-admin"
                                        : "role-user"
                                    }`}
                                  >
                                    {t
                                      ? user.userType === 2
                                        ? t("users.role.admin")
                                        : t("users.role.user")
                                      : user.userType === 2
                                      ? "管理员"
                                      : "用户"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("users.table.password") : "Password"}
                          </span>
                          <span className="cell-value">{user.passWord}</span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("users.table.nickname") : "NickName"}
                          </span>
                          <span className="cell-value">{user.nickName}</span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("users.table.role") : "Role"}
                          </span>
                          <span className="cell-value">
                            {t
                              ? user.userType === 2
                                ? t("users.role.admin")
                                : t("users.role.user")
                              : user.userType === 2
                              ? "管理员"
                              : "用户"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="op-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => {
                              openEditModal({
                                id: user.id || user._id || user.userId || "",
                                userName: user.userName,
                                passWord: user.passWord,
                                NickName: user.nickName,
                              });
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="1em"
                              height="1em"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="#000"
                                fillRule="evenodd"
                                d="m19.902 3.44.659.658c.28.28.439.66.439 1.058v.758c.005.404-.153.793-.44 1.078l-1.845 1.816a.5.5 0 0 1-.708 0l-2.804-2.844a.5.5 0 0 1 0-.699L17.01 3.44A1.5 1.5 0 0 1 18.087 3h.758c.397 0 .777.158 1.057.44m-3.082 7.245a.5.5 0 0 0 0-.7l-2.854-2.813a.5.5 0 0 0-.708 0l-8.052 8.053a2 2 0 0 0-.449.679l-1.716 4.331a.5.5 0 0 0 .19.619.5.5 0 0 0 .538.11l4.33-1.757c.255-.101.486-.254.679-.449z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => {
                              setConfirmDeleteUser({
                                id: user.id || user._id || user.userId || "",
                                name: user.userName,
                              });
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="1em"
                              height="1em"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="#000"
                                fillRule="evenodd"
                                d="M20 5.5v-1a.5.5 0 0 0-.5-.5H15V3a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v1H4.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h15a.5.5 0 0 0 .5-.5M7.72 18h8.56L17 8h2l-.85 12.14a2 2 0 0 1-2 1.86H7.87a2 2 0 0 1-2-1.86L5 8h2z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="users-table-footer">
          <PaginationFooter
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            currentPage={currentPage}
            onPageChange={goToPage}
            totalPages={totalPages}
            goToValue={goToValue}
            onGoToValueChange={onGoToValueChange}
            onGoClick={onGoClick}
          />
        </div>
      </div>
    </div>
  );
};

export default Users;
